import React, { useEffect, useState } from "react";
import { Formik, FieldArray, Field } from "formik";
import Swal from "sweetalert2";
import * as globalSettingsService from "../../../services/globalsetting.services";
import Breadcrums from "../../../common/breadcrumbs";
import {
  DT,
  handleServerValidations,
} from "../../../../../utils/commonfunction";
import { ALLOWED_VIDEO_EXTENSIONS, PHONE_NO_LENGTH, SWAL_SETTINGS } from "../../../../../utils/Constants";
import Loader from "../../../common/loader";
import { URL_REGEX, amountRegex, MOBILE_NUMBER_REGEX } from "../../../../../utils/Constants";
import { useTranslation } from "react-i18next";
import * as Yup from 'yup';
import PhoneInput from "../../../../../utils/PhoneInput";
import { PHONE_VALIDATION } from "../../../../../utils/CommonValidatiobn";
import { Tooltip, Whisper } from "rsuite";

const DEFAULT_THEME_COLORS = {
  primary: "#D4ADF5",
  primaryDark: "#9A45D8",
  primaryLight: "#F4EAFD",
  secondary: "#141414",
  background: "#FFFFFF",
  backgroundSecondary: "#FAF6FE",
  backgroundTertiary: "#F1E4FC",
  surface: "#FFFFFF",
  surfaceMuted: "#F4EAFD",
  headerBackground: "#D4ADF5",
  headerText: "#FFFFFF",
  border: "#E7E7E7",
  borderMuted: "#EEEEEE",
  buttonPrimary: "#D4ADF5",
  buttonPrimaryText: "#FFFFFF",
  buttonPrimaryBorder: "#D4ADF5",
  buttonSecondary: "#141414",
  buttonSecondaryText: "#FFFFFF",
  buttonSecondaryBorder: "#440A44",
  buttonOutline: "#FFFFFF",
  buttonOutlineBorder: "#440A44",
  buttonOutlineText: "#440A44",
  textPrimary: "#141414",
  textSecondary: "#5D5D5D",
  textMuted: "#939393",
  textAccent: "#9A45D8",
  textOnPrimary: "#FFFFFF",
  skeletonBackground: "#F4EAFD",
  skeletonHighlight: "#D4ADF5",
};

// Human-readable labels for each color key
const COLOR_LABELS = {
  primary: "Primary",
  primaryDark: "Primary Dark",
  primaryLight: "Primary Light",
  secondary: "Secondary",
  background: "Background",
  backgroundSecondary: "Background Secondary",
  backgroundTertiary: "Background Tertiary",
  surface: "Surface",
  surfaceMuted: "Surface Muted",
  headerBackground: "Header Background",
  headerText: "Header Text",
  border: "Border",
  borderMuted: "Border Muted",
  buttonPrimary: "Button Primary",
  buttonPrimaryText: "Button Primary Text",
  buttonPrimaryBorder: "Button Primary Border",
  buttonSecondary: "Button Secondary",
  buttonSecondaryBorder: "Button Secondary Border",
  buttonSecondaryText: "Button Secondary Text",
  buttonOutline: "Outline Button",
  buttonOutlineBorder: "Outline Button Border",
  buttonOutlineText: "Outline Button Text",
  textPrimary: "Text Primary",
  textSecondary: "Text Secondary",
  textMuted: "Text Muted",
  textAccent: "Text Accent",
  textOnPrimary: "Text On Primary",
  skeletonBackground: "Skeleton Background",
  skeletonHighlight: "Skeleton Highlight",
};

// Tooltip descriptions for each color key
const COLOR_TOOLTIPS = {
  primary: "Main brand color. Affects: bottom tab selected label, profile photo border, upload profile border, edit profile icons, header notification/logout icon tint, back arrow in TextHeader, subscription plan selected border & price text, Help screen icons, MultiProfile avatar border, loading spinners, tab bar shadow, QR/OTP icons on appointment details, and links/highlights that use brand tint.",
  primaryDark: "Darker brand shade. Affects: Help screen list item icon background (dark purple circle behind help icons). Use for deeper accent areas on top of primary brand.",
  primaryLight: "Soft brand tint background. Affects: modal icon circles (Logout, Delete, Support Chat thank-you), signup autosave notice area, Home category cards border/background, Age selector selected chip background, subscription promo card highlight, Help FAQ icon wrapper, and BBA agreement muted sections.",
  secondary: "Reserved secondary brand color. Currently mapped in theme but not widely wired in UI yet. Intended for alternate brand accents beyond primary.",
  background: "Main screen/page background. Affects: FAQ list scroll area, Edit Profile screen body, Subscription screen scroll background, and any ThemedView with variant='screen'. Also used as bottom part of auth screen gradient.",
  backgroundSecondary: "Secondary soft page background. Affects: Hub tab inactive state, Connections tab inactive, signature type toggle inactive, Home Card background, Signup autosave notice banner, Help screen arrow button background, and segmented tab unselected button fill.",
  backgroundTertiary: "Third-level background tint. Defined in theme tokens but not actively used in themed components yet. Reserved for nested sections/cards.",
  surface: "Card/input/modal white surface. Affects: text inputs (LabelInput), Register form fields, Edit Profile form area, skeleton card wrapper, switch toggle thumb, checkbox unchecked background, TextCheckBox unchecked, Age selector unselected chip, and ThemedView variant='surface'.",
  surfaceMuted: "Muted/disabled surface. Affects: disabled text input background (LabelInput), BBA agreement document preview area, and useThemedStyles surfaceMuted helper. Softer than main surface.",
  headerBackground: "Top header & status areas. Affects: HeaderBar purple strip, TextHeader bar, AuthHeader background, Splash screen background, Android StatusBar color, screen top gradient in Container, Support Chat 'agent ended' banner, and success toast background.",
  headerText: "Text & icons on colored headers. Affects: HeaderBar address/title text, TextHeader title & back area, AuthHeader logo tint and right action text (ThemedText onPrimary), and brand logo on purple headers.",
  border: "Default input/card borders. Affects: LabelInput field border, Register form borders, global inputTitle borders, themed cards, Checkbox border when unchecked, Age selector unselected border, Doula pregnancy stage chips, and TextCheckBox border.",
  borderMuted: "Disabled/subtle borders & OFF states. Affects: Map 'Confirm Location' button when address empty (disabled gray), toggle switches OFF track (TextSwitch, NotificationSettings, Skill forms specialty-care toggle), and Chat quick reply disabled state background.",
  buttonPrimary: "Main action button fill. Affects: AppButton variant='primary' (Continue, Submit, Save, Sign in, etc.), Network retry, chat send button, Scan QR flashlight, Hub/Connections active tab, filter chips, checkboxes when checked, pregnancy/massage modal checkmarks, Other Service radio selected dot, specialty-care toggles ON, Floating filter FAB, Connection card 'View Details', and default IconButton.",
  buttonPrimaryText: "Text on primary buttons. Affects: all AppButton/IconButton with variant='primary', Hub & Connections active tab label, Connection card button label, Chat quick reply selected text, and 'Track babe Associate' label on appointment map.",
  buttonPrimaryBorder: "Border color of primary buttons. Affects: AppButton variant='primary' border ring on all primary CTAs across modals, agreements, and secondary actions.",
  buttonSecondary: "Secondary/dark action button fill. Affects: AppButton variant='secondary' (Sign up, Cancel, View Agreement, black CTAs), IconButton variant='secondary' (Play, verification buttons), and appointment booking card Chat/Call circular buttons.",
  buttonSecondaryText: "Text on secondary (dark) buttons. Affects: all AppButton and IconButton with variant='secondary' — e.g. Welcome 'Sign up with phone', BBA 'View Agreement', Searching Associate 'Cancel Search'.",
  buttonSecondaryBorder: "Border color of secondary buttons. Affects: AppButton variant='secondary' border ring on all secondary CTAs across modals, agreements, and secondary actions.",
  buttonOutline: "Outline button background (usually white/transparent fill). Affects: AppButton variant='outline' — Cancel modals, No/Back actions, Remove job, Scan QR Retry, App Update Cancel, On-Call 'No', subscription 'Continue with Promo code'.",
  buttonOutlineBorder: "Border color of outline buttons. Affects: AppButton variant='outline' border ring on all outline CTAs across modals, agreements, and secondary actions.",
  buttonOutlineText: "Text color on outline buttons. Affects: label text inside all AppButton/IconButton with variant='outline' (Cancel, No, Remove, Retry, etc.).",
  textPrimary: "Main body/heading text. Affects: ThemedText variant='primary', screen titles (Register, Bio, Location, Agreements), FAQ titles, Edit Profile labels, NoDataText title, global inputTitle, Register field values, Home Card title, and most dark readable text.",
  textSecondary: "Supporting/subtitle text. Affects: ThemedText variant='secondary', form subtitles under headings, FAQ descriptions, Edit Profile secondary labels, Hub/Connections inactive tab text, Register helper text, LabelInput titles, and Chat quick reply unselected text.",
  textMuted: "Placeholder & low-emphasis text. Affects: ThemedText variant='muted', input placeholders (LabelInput, Register), bottom tab inactive labels, Register date/empty field hint color, and Signup autosave notice text.",
  textAccent: "Accent/highlight text links. Affects: ThemedText variant='accent', Age selector selected chip text, Support Chat thank-you redirect text, Contact form 'Support Chat' link, subscription plan subtitle/price accent lines, and Help screen highlighted text.",
  textOnPrimary: "Text/icons on purple/colored backgrounds. Affects: ThemedText variant='onPrimary' on AuthHeader right actions, Help screen icons on purple circles (tint), and any text sitting directly on headerBackground areas.",
  skeletonBackground: "Loading skeleton base color. Affects: all ThemedSkeletonPlaceholder loaders — appointment details skeleton, chat skeleton, category loader, home banners skeleton, accept modal skeleton, and any shimmer placeholder base.",
  skeletonHighlight: "Loading skeleton shimmer/highlight color. Affects: animated highlight wave on all ThemedSkeletonPlaceholder components while content is loading across the app.",
};

const GlobalSettings = (props) => {
  const { t } = useTranslation();
  const [showdefault, setShowDefault] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [submitClicked, setSubmitClicked] = useState(false);
  const breadcrumbs = [
    { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
    { title: t("sidebar_link_global_settings"), url: "" },
  ];
  const [refresh, setRefresh] = useState(false);


  const videoExtensionRegex = new RegExp(
    `(${ALLOWED_VIDEO_EXTENSIONS.map(ext => `\\${ext}`).join('|')})$`,
    'i'
  );

  useEffect(() => {
    globalSettingsService
      .Details()
      .then((response) => {
        setShowDefault(response && response.data ? response?.data : []);
        setTimeout(() => {
          setDataLoaded(true);
        }, 100);
      })
      .catch((error) => {
        console.log("error=====>", error);
        setDataLoaded(true);
      });
  }, [refresh]);

  const validationSchema = Yup.object().shape({
    socialMedia: Yup.array().of(
      Yup.object().shape({
        title: Yup.string().trim().required("validation_err_title_required").max(20, "validation_err_reached_maximum_length"),
        icon: Yup.string().trim().required("validation_err_icon_required").max(50, "validation_err_reached_maximum_length"),
        link: Yup.string().trim().required("validation_err_link_required").matches(URL_REGEX, "invalid_web_url"),
      })
    ),
    AdminEmail: Yup.string().trim().required("label_email_error").email("invalid_email"),

    phone_number: PHONE_VALIDATION, // Already handles trimming internally if needed

    CopyRighttxt: Yup.string().trim().required("copyright_error"),

    Address: Yup.string().trim().required("label_location_error").max(100, "validation_err_reached_maximum_length"),

    title: Yup.string().trim().required("label_title_error").max(50, "label_title_length_error_50"),

    video_link: Yup.string().trim().required("Video link is required"),

    testing_phone: Yup.string().trim().required("Testing phone number is required"),

    play_store_url: Yup.string().trim().required("Play store link is required"),

    brand_video_url: Yup.string().trim().required("Brand video is required"),

    app_store_url: Yup.string().trim().required("App store link is required"),

    fertility_injection_category_id : Yup.string().trim().required("Fertility injection category id is required"),

    android_latest_version: Yup.string().trim().required("Android latest version is required"),
    android_minimum_version: Yup.string().trim().required("Android minimum version is required"),
    ios_latest_version: Yup.string().trim().required("iOS latest version is required"),
    ios_minimum_version: Yup.string().trim().required("iOS minimum version is required"),

    min_hours_before_booking: Yup.number()
      .typeError("Minimum hours before booking must be a number")
      .required("Minimum hours before booking is required")
      .integer("Only whole numbers are allowed")
      .min(1, "Minimum value must be at least 1")
      .max(100, "Maximum value can be 100"),

    pre_booking_time: Yup.number()
      .typeError("Pre-appointment lock time must be a number")
      .required("Pre-appointment lock time is required")
      .integer("Only whole numbers are allowed")
      .min(1, "Pre-appointment lock must be at least 1 hour")
      .max(10, "Pre-appointment lock cannot exceed 10 hours"),

    post_booking_time: Yup.number()
      .typeError("Post-appointment lock time must be a number")
      .required("Post-appointment lock time is required")
      .integer("Only whole numbers are allowed")
      .min(1, "Post-appointment lock must be at least 1 hour")
      .max(10, "Post-appointment lock cannot exceed 10 hours"),
  });

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      {dataLoaded ? (
        <Formik
          validateOnChange={true}
          enableReinitialize
          initialValues={{
            Address: showdefault && showdefault.address ? showdefault.address : "",

            AdminEmail: showdefault && showdefault.admin_email ? showdefault.admin_email : "",
            phone_number: showdefault && showdefault.phone_number ? showdefault.phone_number : "",
            CopyRighttxt: showdefault && showdefault.copy_right_text ? showdefault.copy_right_text : "",
            title: showdefault && showdefault.title ? showdefault.title : "",

            socialMedia: showdefault && showdefault.social_media_details && showdefault?.social_media_details?.length ? showdefault.social_media_details : [{ title: "", link: "", icon: "" }],
            video_link: showdefault && showdefault.video_link ? showdefault.video_link : "",
            play_store_url: showdefault && showdefault.play_store_url ? showdefault.play_store_url : "",
            app_store_url: showdefault && showdefault.app_store_url ? showdefault.app_store_url : "",
            brand_video_url: showdefault && showdefault.app_store_url ? showdefault.brand_video_url : "",
            testing_phone: showdefault?.testing_phone ? showdefault.testing_phone.join(", ") : "",
            banner_visibility: showdefault && showdefault.banner_visibility ? showdefault.banner_visibility : false,
            maintenance_mode: showdefault && showdefault.maintenance_mode ? showdefault.maintenance_mode : false,
            show_subscriptions: showdefault && showdefault.show_subscriptions ? showdefault.show_subscriptions : false,
            min_hours_before_booking: showdefault && showdefault.min_hours_before_booking ? showdefault.min_hours_before_booking : 0,
            pre_booking_time: showdefault && showdefault.pre_booking_time ? showdefault.pre_booking_time : 0,
            post_booking_time: showdefault && showdefault.post_booking_time ? showdefault.post_booking_time : 0,
            fertility_injection_category_id : showdefault && showdefault.fertility_injection_category_id ? showdefault.fertility_injection_category_id : "",
            android_latest_version: showdefault?.android_latest_version || "",
            android_minimum_version: showdefault?.android_minimum_version || "",
            ios_latest_version: showdefault?.ios_latest_version || "",
            ios_minimum_version: showdefault?.ios_minimum_version || "",
            theme_colors: showdefault?.theme_colors
              ? { ...DEFAULT_THEME_COLORS, ...showdefault.theme_colors }
              : { ...DEFAULT_THEME_COLORS },
          }}
          validationSchema={validationSchema}
          onSubmit={(values, { setSubmitting, resetForm }) => {
            setSubmitted(true);

            // Trimmed version of all fields
            const trimmedValues = {
              Address: values.Address.trim(),
              AdminEmail: values.AdminEmail.trim(),
              phone_number: values.phone_number.trim(),
              CopyRighttxt: values.CopyRighttxt.trim(),
              title: values.title.trim(),
              video_link: values.video_link.trim(),
              play_store_url: values.play_store_url.trim(),
              app_store_url: values.app_store_url.trim(),
              brand_video_url: values.brand_video_url.trim(),
              socialMedia: values.socialMedia.map((item) => ({
                title: item.title.trim(),
                link: item.link.trim(),
                icon: item.icon.trim(),
              })),
              testing_phone: values.testing_phone
                ? values.testing_phone
                  .split(",")
                  .map(num => num.trim())
                  .filter(num => num.length > 0)
                : [],
              fertility_injection_category_id: values.fertility_injection_category_id.trim(),
              android_latest_version: values.android_latest_version.trim(),
              android_minimum_version: values.android_minimum_version.trim(),
              ios_latest_version: values.ios_latest_version.trim(),
              ios_minimum_version: values.ios_minimum_version.trim(),
            };
            
            let formData = new FormData();
            formData.append("address", trimmedValues.Address);
            formData.append("admin_email", trimmedValues.AdminEmail);
            formData.append("social_media_details", JSON.stringify(trimmedValues.socialMedia));
            formData.append("phone_number", trimmedValues.phone_number);
            formData.append("copy_right_text", trimmedValues.CopyRighttxt);
            formData.append("title", trimmedValues.title);
            formData.append("video_link", trimmedValues.video_link);
            formData.append("play_store_url", trimmedValues.play_store_url);
            formData.append("app_store_url", trimmedValues.app_store_url);
            formData.append("brand_video_url", trimmedValues.brand_video_url);
            formData.append("banner_visibility", values.banner_visibility);
            formData.append("maintenance_mode", values.maintenance_mode);
            formData.append("show_subscriptions", values.show_subscriptions);
            formData.append("testing_phone", values.testing_phone);
            formData.append("min_hours_before_booking", values.min_hours_before_booking);
            formData.append("pre_booking_time", values.pre_booking_time);
            formData.append("post_booking_time", values.post_booking_time);
            formData.append("fertility_injection_category_id", trimmedValues.fertility_injection_category_id);
            formData.append("android_latest_version", trimmedValues.android_latest_version);
            formData.append("android_minimum_version", trimmedValues.android_minimum_version);
            formData.append("ios_latest_version", trimmedValues.ios_latest_version);
            formData.append("ios_minimum_version", trimmedValues.ios_minimum_version);
            formData.append("theme_colors", JSON.stringify(values.theme_colors));

            // console.log("formData--->",formData)
            globalSettingsService
              .Add(formData)
              .then((response) => {
                if (response.success) {
                  setRefresh((prev) => !prev);
                  Swal.fire({
                    icon: "success",
                    text: response.message,
                    ...SWAL_SETTINGS,
                  });
                  setTimeout(() => {
                    setSubmitted(false);
                  }, 2000);
                } else {
                  Swal.fire({
                    icon: "error",
                    text: handleServerValidations(response),
                    ...SWAL_SETTINGS,
                  });
                  setSubmitted(false);
                }
              })
              .catch((error) => {
                console.log("error ====> ", error);
              });
          }}
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, isSubmitting, setFieldTouched }) => (
            <form onSubmit={handleSubmit}>
              {/* {console.log("error", errors)} */}
              <div className="row row-sm">
                <div className="col-lg-12 col-md-12 animation_fade">
                  <div className="card custom-card">
                    <div className="card-body">
                      <div>
                        <h6 className="main-content-label mb-3">{t("sidebar_link_global_settings")}</h6>
                      </div>
                      <div className="row row-sm">
                        <div className="col-md-6 text-center form-group">
                          <label htmlFor="title" className="text-left d-flex">
                            {t("title")}:<span className="requirestar">*</span>{" "}
                            <Whisper
                              placement="top"
                              controlId="control-id-hover"
                              trigger="hover"
                              speaker={<Tooltip style={{ fontWeight: "bold" }}>This title will be show as marketing website title </Tooltip>}
                            >
                              <span className="field-more-info  ms-1 cp" aria-describedby="control-id-hover">
                                ?
                              </span>
                            </Whisper>
                          </label>
                          <input name="title" type="text" id="title" onChange={handleChange} onBlur={handleBlur} value={values.title} className="form-control" placeholder={t("Enter title")} />
                          <span className="text-danger d-flex text-left">{errors.title && touched.title && t(errors.title)}</span>
                        </div>

                        <div className="col-md-6 text-center form-group">
                          <label htmlFor="video_link" className="text-left d-flex">
                            {t("label_video_link")}:<span className="requirestar">*</span>{" "}
                          </label>
                          <input
                            name="video_link"
                            id="video_link"
                            type="text"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.video_link}
                            className="form-control"
                            placeholder={t("video_link_placeholder")}
                          />
                          <span className="text-danger d-flex text-left">{errors.video_link && touched.video_link && t(errors.video_link)}</span>
                        </div>

                        <div className="col-md-6 text-center form-group">
                          <label htmlFor="play_store_url" className="text-left d-flex">
                            {t("label_play_store_url")}:<span className="requirestar">*</span>{" "}
                          </label>
                          <input
                            name="play_store_url"
                            id="play_store_url"
                            type="text"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.play_store_url}
                            className="form-control"
                            placeholder={t("play_store_url_placeholder")}
                          />
                          <span className="text-danger d-flex text-left">{errors.play_store_url && touched.play_store_url && t(errors.play_store_url)}</span>
                        </div>

                        <div className="col-md-6 text-center form-group">
                          <label htmlFor="app_store_url" className="text-left d-flex">
                            {t("label_app_store_url")}:<span className="requirestar">*</span>{" "}
                          </label>
                          <input
                            name="app_store_url"
                            id="app_store_url"
                            type="text"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.app_store_url}
                            className="form-control"
                            placeholder={t("app_store_url_placeholder")}
                          />
                          <span className="text-danger d-flex text-left">{errors.app_store_url && touched.app_store_url && t(errors.app_store_url)}</span>
                        </div>

                        <div className="col-md-6 text-center form-group">
                          <label htmlFor="brand_video_url" className="text-left d-flex">
                            {t("label_brand_video_url")}:<span className="requirestar">*</span>{" "}
                          </label>
                          <input
                            name="brand_video_url"
                            id="brand_video_url"
                            type="text"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.brand_video_url}
                            className="form-control"
                            placeholder={t("brand_video_url_placeholder")}
                          />
                          <span className="text-danger d-flex text-left">{errors.brand_video_url && touched.brand_video_url && t(errors.brand_video_url)}</span>
                        </div>

                        <div className="col-md-6 text-center form-group">
                          <label htmlFor="CopyRighttxt" className="text-left d-flex">
                            {t("label_copy_right_text")}:<span className="requirestar">*</span>{" "}
                          </label>
                          <input
                            name="CopyRighttxt"
                            type="text"
                            id="CopyRighttxt"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.CopyRighttxt}
                            className="form-control"
                            placeholder={t("Enter copy right text")}
                          />
                          <span className="text-danger d-flex text-left">{errors.CopyRighttxt && touched.CopyRighttxt && t(errors.CopyRighttxt)}</span>
                        </div>

                        <div className="col-md-6 text-center form-group">
                          <label htmlFor="AdminEmail" className="text-left d-flex">
                            {t("label_support")} {t("label_email")}:<span className="requirestar">*</span>
                          </label>
                          <input
                            name="AdminEmail"
                            type="text"
                            id="AdminEmail"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.AdminEmail}
                            className="form-control"
                            placeholder={t("placeholder_email_address")}
                          />
                          <span className="text-danger d-flex text-left">{errors.AdminEmail && touched.AdminEmail && t(errors.AdminEmail)}</span>
                        </div>

                        <div className="col-md-6 text-center form-group">
                          <label htmlFor="phone_number" className="text-left d-flex">
                            {t("label_support")} {t("label_contact_no")}:<span className="requirestar">*</span>
                          </label>
                          <PhoneInput
                            name="phone_number"
                            type="text"
                            id="phone_number"
                            onChange={(e) => {
                              setFieldValue("phone_number", e?.phone_number);
                            }}
                            // onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.phone_number}
                            className="form-control"
                            placeholder={t("web_inquiry_form_placeholder_contact_number")}
                          />
                          <span className="text-danger d-flex text-left">
                            {errors.phone_number && touched.phone_number && DT(t(errors.phone_number), { PHONE_MIN_LENGTH: PHONE_NO_LENGTH.min, PHONE_MAX_LENGTH: PHONE_NO_LENGTH.max })}
                          </span>
                        </div>

                        <div className="col-md-6 text-center form-group">
                          <label htmlFor="AdminEmail" className="text-left d-flex">
                            Minimum Hours Before Appointment Booking:<span className="requirestar">*</span>
                          </label>
                          <input
                            name="min_hours_before_booking"
                            type="text"
                            id="min_hours_before_booking"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.min_hours_before_booking}
                            className="form-control"
                            placeholder="Enter Hours"
                          />
                          <span className="text-danger d-flex text-left">{errors.min_hours_before_booking && touched.min_hours_before_booking && t(errors.min_hours_before_booking)}</span>
                        </div>

                        <div className="col-md-3 text-center form-group">
                          {/* Flex container for two toggles */}
                          <div className="d-flex  align-items-center">
                            {/* Toggle One */}
                            <div className="d-flex align-items-start flex-column">
                              <label className="text-left d-flex ">Show Banners </label>
                              <label className={"custom-switch cp mt-2" + (values.banner_visibility === true ? " e_none" : "")}>
                                <input className="custom-switch-input cp" name="banner_visibility" type="checkbox" onChange={handleChange} onBlur={handleBlur} value={values.banner_visibility} checked={values?.banner_visibility === true} />
                                <span className="custom-switch-indicator cp"></span>
                              </label>
                            </div>

                          </div>
                        </div>
                        <div className="col-md-3 text-center form-group">
                          {/* Flex container for two toggles */}
                          <div className="d-flex  align-items-center">

                            {/* Toggle Two */}
                            <div className="d-flex align-items-start flex-column">
                              <label className="text-left d-flex">Maintenance Mode</label>
                              <label className={"custom-switch cp mt-2" + (values.maintenance_mode === true ? " e_none" : "")}>
                                <input className="custom-switch-input cp" name="maintenance_mode" type="checkbox" onChange={handleChange} onBlur={handleBlur} value={values.maintenance_mode} checked={values?.maintenance_mode === true} />
                                <span className="custom-switch-indicator cp"></span>
                              </label>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6 text-center form-group">
                          <label htmlFor="AdminEmail" className="text-left d-flex">
                          Pre-Appointment Lock:<span className="requirestar">*</span>
                          </label>
                          <input
                            name="pre_booking_time"
                            type="text"
                            id="pre_booking_time"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.pre_booking_time}
                            className="form-control"
                            placeholder="Enter Hours"
                          />
                          <span className="text-danger d-flex text-left">{errors.pre_booking_time && touched.pre_booking_time && t(errors.pre_booking_time)}</span>
                        </div>
                        <div className="col-md-6 text-center form-group">
                          <label htmlFor="AdminEmail" className="text-left d-flex">
                          Post-Appointment Lock:<span className="requirestar">*</span>
                          </label>
                          <input
                            name="post_booking_time"
                            type="text"
                            id="post_booking_time"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.post_booking_time}
                            className="form-control"
                            placeholder="Enter Hours"
                          />
                          <span className="text-danger d-flex text-left">{errors.post_booking_time && touched.post_booking_time && t(errors.post_booking_time)}</span>
                        </div>
                        <div className="col-md-3 text-center form-group">
                          {/* Flex container for two toggles */}
                          <div className="d-flex  align-items-center">

                            {/* Toggle Two */}
                            <div className="d-flex align-items-start flex-column">
                              <label className="text-left d-flex">Show Subscription</label>
                              <label className={"custom-switch cp mt-2" + (values.show_subscriptions === true ? " e_none" : "")}>
                                <input className="custom-switch-input cp" name="show_subscriptions" type="checkbox" onChange={handleChange} onBlur={handleBlur} value={values.show_subscriptions} checked={values?.show_subscriptions === true} />
                                <span className="custom-switch-indicator cp"></span>
                              </label>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-12 text-center form-group">
                          <label htmlFor="Address" className="text-left d-flex">
                            {t("label_support")} {t("list_heading_address")}:<span className="requirestar">*</span>{" "}
                          </label>
                          <textarea
                            name="Address"
                            id="Address"
                            type="text"
                            style={{ minHeight: "100px" }}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.Address}
                            className="form-control"
                            placeholder={t("placeholder_address")}
                          />
                          <span className="text-danger d-flex text-left">{errors.Address && touched.Address && t(errors.Address)}</span>
                        </div>

                        <div className="col-md-12 text-center form-group">
                          <label htmlFor="testing_phone" className="text-left d-flex">
                            {t("test_phone")}:<span className="requirestar">*</span>{" "}
                          </label>
                          <textarea
                            name="testing_phone"
                            id="testing_phone"
                            type="text"
                            style={{ minHeight: "100px" }}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.testing_phone}
                            className="form-control"
                            placeholder={t("placeholder_testing_phone")}
                          />
                          <span className="text-danger d-flex text-left">{errors.testing_phone && touched.testing_phone && t(errors.testing_phone)}</span>
                        </div>

                        <div className="col-md-12 text-center form-group">
                          <label htmlFor="fertility_injection_category_id" className="text-left d-flex">
                            {t("Fertility Injection Category ID")}:<span className="requirestar">*</span>{" "}
                          </label>
                          <input
                            name="fertility_injection_category_id"
                            id="fertility_injection_category_id"
                            type="text"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.fertility_injection_category_id}
                            className="form-control"
                            placeholder={t("Enter Fertility Injection Category ID")}
                          />
                          <span className="text-danger d-flex text-left">{errors.fertility_injection_category_id && touched.fertility_injection_category_id && t(errors.fertility_injection_category_id)}</span>
                        </div>

                        <div className="col-md-6 text-center form-group">
                          <label htmlFor="android_latest_version" className="text-left d-flex">
                            Android Latest Version:<span className="requirestar">*</span>
                          </label>
                          <input
                            name="android_latest_version"
                            id="android_latest_version"
                            type="text"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.android_latest_version}
                            className="form-control"
                            placeholder="e.g. 1.0.0"
                          />
                          <span className="text-danger d-flex text-left">{errors.android_latest_version && touched.android_latest_version && errors.android_latest_version}</span>
                        </div>

                        <div className="col-md-6 text-center form-group">
                          <label htmlFor="android_minimum_version" className="text-left d-flex">
                            Android Minimum Version:<span className="requirestar">*</span>
                          </label>
                          <input
                            name="android_minimum_version"
                            id="android_minimum_version"
                            type="text"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.android_minimum_version}
                            className="form-control"
                            placeholder="e.g. 1.0.0"
                          />
                          <span className="text-danger d-flex text-left">{errors.android_minimum_version && touched.android_minimum_version && errors.android_minimum_version}</span>
                        </div>

                        <div className="col-md-6 text-center form-group">
                          <label htmlFor="ios_latest_version" className="text-left d-flex">
                            iOS Latest Version:<span className="requirestar">*</span>
                          </label>
                          <input
                            name="ios_latest_version"
                            id="ios_latest_version"
                            type="text"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.ios_latest_version}
                            className="form-control"
                            placeholder="e.g. 1.0.0"
                          />
                          <span className="text-danger d-flex text-left">{errors.ios_latest_version && touched.ios_latest_version && errors.ios_latest_version}</span>
                        </div>

                        <div className="col-md-6 text-center form-group">
                          <label htmlFor="ios_minimum_version" className="text-left d-flex">
                            iOS Minimum Version:<span className="requirestar">*</span>
                          </label>
                          <input
                            name="ios_minimum_version"
                            id="ios_minimum_version"
                            type="text"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.ios_minimum_version}
                            className="form-control"
                            placeholder="e.g. 1.0.0"
                          />
                          <span className="text-danger d-flex text-left">{errors.ios_minimum_version && touched.ios_minimum_version && errors.ios_minimum_version}</span>
                        </div>

                        <label className="text-left d-flex">{t("label_social_media")}:</label>
                        <FieldArray
                          name="socialMedia"
                          render={(arrayHelper) => (
                            <div className="col-12 mb-4">
                              <div className="row">
                                {values?.socialMedia && values?.socialMedia?.length >= 0 ? (
                                  values?.socialMedia.map((item, index) => (
                                    <div className="col-12 " key={index}>
                                      <div className="w-100 p-2 bg-group">
                                        <div className="row">
                                          <div className="col-md-3 text-center form-group mb-0">
                                            <Field
                                              className="form-control"
                                              id="title"
                                              name={`socialMedia[${index}].title`}
                                              onChange={handleChange}
                                              onBlur={handleBlur}
                                              value={item.title}
                                              placeholder={t("input_placeholder_title")}
                                            />
                                            <span className="text-danger d-flex text-left">
                                              {((touched?.socialMedia && touched?.socialMedia[index] && touched?.socialMedia[index].title) || submitClicked) &&
                                                errors.socialMedia &&
                                                errors.socialMedia[index] &&
                                                t(errors.socialMedia[index].title)}
                                            </span>
                                          </div>
                                          <div className="col-md-4 text-center form-group mb-0">
                                            <Field
                                              className="form-control"
                                              id="link"
                                              name={`socialMedia[${index}].link`}
                                              onChange={handleChange}
                                              onBlur={handleBlur}
                                              value={item.link}
                                              placeholder={t("input_placeholder_link")}
                                            />
                                            <span className="text-danger d-flex text-left">
                                              {((touched.socialMedia && touched.socialMedia[index] && touched.socialMedia[index].link) || submitClicked) &&
                                                errors.socialMedia &&
                                                errors.socialMedia[index] &&
                                                t(errors.socialMedia[index].link)}
                                            </span>
                                          </div>
                                          <div className="col-md-3 text-center form-group mb-0">
                                            <Field
                                              className="form-control"
                                              id="icon"
                                              name={`socialMedia[${index}].icon`}
                                              onChange={handleChange}
                                              onBlur={handleBlur}
                                              value={item.icon}
                                              placeholder={t("input_placeholder_icon")}
                                            />
                                            <span className="text-danger d-flex text-left">
                                              {((touched.socialMedia && touched.socialMedia[index] && touched.socialMedia[index].icon) || submitClicked) &&
                                                errors.socialMedia &&
                                                errors.socialMedia[index] &&
                                                t(errors.socialMedia[index].icon)}
                                            </span>
                                          </div>
                                          <div className="col-md-2">
                                            <div className="social-media-action">
                                              <button
                                                className="btn btn-danger "
                                                type="button"
                                                disabled={values?.socialMedia?.length === 1}
                                                onClick={() => arrayHelper.remove(index)} // remove a friend from the list
                                              >
                                                {t("link_remove")}
                                              </button>
                                              {index === values?.socialMedia?.length - 1 && (
                                                <button
                                                  className="btn btn-main-primary signbtn ms-2 "
                                                  type="button"
                                                  onClick={() =>
                                                    arrayHelper.insert(values?.socialMedia?.length, {
                                                      title: "",
                                                      link: "",
                                                      icon: "",
                                                    })
                                                  }
                                                >
                                                  {t("link_add")}
                                                </button>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <button className="btn btn-main-primary signbtn mr-2 ms-2 mb-3" type="button" onClick={() => arrayHelper.push({})}>
                                    Add a social media
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        />

                        {/* ── App Theme Colors ─────────────────────────────── */}
                        <div className="col-md-12 mt-4 mb-2">
                          <div className="d-flex align-items-center justify-content-between mb-3">
                            <h6 className="main-content-label mb-0">App Theme Colors</h6>
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm"
                              onClick={() => setFieldValue("theme_colors", { ...DEFAULT_THEME_COLORS })}
                            >
                              <i className="fa fa-undo me-1"></i> Reset to Default
                            </button>
                          </div>
                          <div className="row row-sm">
                            {Object.keys(DEFAULT_THEME_COLORS).map((colorKey) => (
                              <div className="col-md-3 col-sm-6 form-group" key={colorKey}>
                                <label className="text-left d-flex align-items-center gap-2 mb-1" style={{ fontSize: "0.85rem" }}>
                                  <span
                                    style={{
                                      display: "inline-block",
                                      width: 16,
                                      height: 16,
                                      borderRadius: 3,
                                      background: values.theme_colors?.[colorKey] || DEFAULT_THEME_COLORS[colorKey],
                                      border: "1px solid #ccc",
                                      flexShrink: 0,
                                    }}
                                  />
                                  {COLOR_LABELS[colorKey]}
                                  {COLOR_TOOLTIPS[colorKey] && (
                                    <Whisper
                                      placement="top"
                                      trigger="hover"
                                      speaker={
                                        <Tooltip style={{ maxWidth: 280, whiteSpace: "normal", textAlign: "left" }}>
                                          {COLOR_TOOLTIPS[colorKey]}
                                        </Tooltip>
                                      }
                                    >
                                      <span className="field-more-info ms-1 cp" style={{ fontSize: "0.75rem" }}>?</span>
                                    </Whisper>
                                  )}
                                </label>
                                <div className="d-flex align-items-center gap-2">
                                  <input
                                    type="color"
                                    value={values.theme_colors?.[colorKey] || DEFAULT_THEME_COLORS[colorKey]}
                                    onChange={(e) =>
                                      setFieldValue("theme_colors", {
                                        ...values.theme_colors,
                                        [colorKey]: e.target.value,
                                      })
                                    }
                                    style={{ width: 40, height: 36, padding: 2, border: "1px solid #ccc", borderRadius: 4, cursor: "pointer" }}
                                  />
                                  <input
                                    type="text"
                                    value={values.theme_colors?.[colorKey] || DEFAULT_THEME_COLORS[colorKey]}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      if (/^#[0-9A-Fa-f]{0,6}$/.test(val)) {
                                        setFieldValue("theme_colors", {
                                          ...values.theme_colors,
                                          [colorKey]: val,
                                        });
                                      }
                                    }}
                                    className="form-control form-control-sm"
                                    style={{ fontFamily: "monospace", maxWidth: 100 }}
                                    maxLength={7}
                                    placeholder="#000000"
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        {/* ── End Theme Colors ─────────────────────────────── */}

                        {/* {hasPermission("/admin/cms/settings/submit") ? ( */}
                        <div className="">
                          <button
                            className="btn btn-main-primary signbtn mr-2"
                            type="submit"
                            disabled={submitted ? true : null}
                            onClick={() => {
                              setSubmitClicked(true);
                            }}
                          >
                            <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                            {t("btn_submit")}
                          </button>
                        </div>
                        {/* ) : null} */}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          )}
        </Formik>
      ) : (
        <Loader />
      )}
    </>
  );
};

export default GlobalSettings;


